<template>
  <div class="min-h-screen bg-[#181818] flex flex-col">
    <!-- Header -->
    <header
      class="relative border-b border-[#F5BE32] p-4 md:p-8 lg:p-16 flex-shrink-0"
      :style="headerStyle"
    >
      <div
        class="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0"
      >
        <div class="flex items-center gap-2 md:gap-3">
          <router-link to="/" class="cursor-pointer">
            <img
              src="@/assets/logo.png"
              alt="Tuscany House Logo"
              class="w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 rounded-full hover:scale-110 transition"
            />
          </router-link>
          <h1
            class="text-lg md:text-xl lg:text-2xl font-bold text-[#F5BE32] font-serif"
          >
            {{ $t("chef.title") }}
          </h1>
        </div>

        <div class="flex items-center gap-2 md:gap-4">
          <button
            @click="handleLogout"
            class="px-3 py-1 md:px-4 md:py-2 text-sm md:text-base border border-[#F5BE32] text-white rounded hover:bg-[#F5BE32] hover:text-black transition"
          >
            {{ $t("chef.logout") }}
          </button>
          <img
            src="@/assets/icons/goldTranslate.svg"
            alt="Translate"
            class="w-12 h-6 md:w-16 md:h-8 cursor-pointer hover:scale-110 transition"
            @click="toggleLang"
          />
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="p-3 md:p-6 flex-1">
      <!-- Loading -->
      <div
        v-if="loading"
        class="flex justify-center items-center py-8 md:py-12"
      >
        <div class="text-[#F5BE32] text-lg md:text-xl">
          {{ $t("chef.loading") }}
        </div>
      </div>

      <!-- Orders Content -->
      <div v-else>
        <!-- Today's Orders Section -->
        <div class="mb-8">
          <div class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
            <div class="flex items-center gap-2 md:gap-3">
              <img
                src="@/assets/icons/ListChecks.svg"
                alt="List Checks"
                class="w-8 h-6 md:w-10 md:h-8"
              />
              <h2 class="text-xl md:text-2xl lg:text-3xl font-bold text-[#F5BE32]">
                {{ $t("chef.todaysOrders") }}
              </h2>
            </div>

            <div
              class="flex flex-col sm:flex-row gap-2 md:gap-3 w-full lg:w-auto relative"
            >
              <!-- Order Type Filter -->
              <div>
                <button
                  @click="toggleOrderTypeFilter"
                  class="w-full sm:w-auto px-3 py-2 md:px-4 md:py-2 text-sm md:text-base border border-[#F5BE32] text-white rounded flex items-center justify-between sm:justify-start gap-2 hover:bg-[#F5BE32] hover:text-black transition"
                >
                                     <span>{{ getOrderTypeFilterText(selectedOrderTypeFilter) || $t("chef.orderType") }}</span>
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              <!-- Duration Filter -->
              <div>
                <button
                  @click="toggleDurationFilter"
                  class="w-full sm:w-auto px-3 py-2 md:px-4 md:py-2 text-sm md:text-base border border-[#F5BE32] text-white rounded flex items-center justify-between sm:justify-start gap-2 hover:bg-[#F5BE32] hover:text-black transition"
                >
                                     <span>{{ getDurationFilterText(selectedDurationFilter) || $t("chef.duration") }}</span>
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              <!-- Status Filter -->
              <div>
                <button
                  @click="toggleStatusFilter"
                  class="w-full sm:w-auto px-3 py-2 md:px-4 md:py-2 text-sm md:text-base border border-[#F5BE32] text-white rounded flex items-center justify-between sm:justify-start gap-2 hover:bg-[#F5BE32] hover:text-black transition"
                >
                                     <span>{{ getStatusFilterText(selectedStatusFilter) || $t("chef.status") }}</span>
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              <!-- Shared Dropdown Container -->
              <div
                v-if="
                  showOrderTypeFilterDropdown ||
                  showDurationFilterDropdown ||
                  showStatusFilterDropdown
                "
                class="absolute left-1/2 transform -translate-x-1/2 top-full mt-2 w-48 bg-[#1F1F1F] border border-[#F5BE32] rounded shadow-lg z-10 overflow-x-hidden"
              >
                <div class="py-1">
                  <!-- Order Type Filter Options -->
                  <div v-if="showOrderTypeFilterDropdown">
                    <button
                      @click="setOrderTypeFilter('')"
                      class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                    >
                      {{ $t("chef.allOrderTypes") }}
                    </button>
                    <button
                      @click="setOrderTypeFilter('preorder')"
                      class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                    >
                      {{ $t("chef.preorder") }}
                    </button>
                    <button
                      @click="setOrderTypeFilter('delivery')"
                      class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                    >
                      {{ $t("chef.delivery") }}
                    </button>
                    <button
                      @click="setOrderTypeFilter('dineIn')"
                      class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                    >
                      {{ $t("chef.dineIn") }}
                    </button>
                  </div>

                  <!-- Duration Filter Options -->
                  <div v-if="showDurationFilterDropdown">
                    <button
                      @click="setDurationFilter('')"
                      class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                    >
                      {{ $t("chef.allDurations") }}
                    </button>
                    <button
                      @click="setDurationFilter('breakfast')"
                      class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                    >
                      {{ $t("chef.breakfast") }}
                    </button>
                    <button
                      @click="setDurationFilter('lunch')"
                      class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                    >
                      {{ $t("chef.lunch") }}
                    </button>
                    <button
                      @click="setDurationFilter('dinner')"
                      class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                    >
                      {{ $t("chef.dinner") }}
                    </button>
                  </div>

                  <!-- Status Filter Options -->
                  <div v-if="showStatusFilterDropdown">
                    <button
                      @click="setStatusFilter('')"
                      class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                    >
                      {{ $t("chef.allStatuses") }}
                    </button>
                                         <button
                       @click="setStatusFilter('Not Started')"
                       class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                     >
                       {{ $t("chef.notStarted") }}
                     </button>
                     <button
                       @click="setStatusFilter('In Progress')"
                       class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                     >
                       {{ $t("chef.inProgress") }}
                     </button>
                     <button
                       @click="setStatusFilter('Served')"
                       class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                     >
                       {{ $t("chef.served") }}
                     </button>
                     <button
                       @click="setStatusFilter('pending')"
                       class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                     >
                       {{ $t("admin.orders.statuses.pending") }}
                     </button>
                     <button
                       @click="setStatusFilter('preparing')"
                       class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                     >
                       {{ $t("admin.orders.statuses.preparing") }}
                     </button>
                     <button
                       @click="setStatusFilter('confirmed')"
                       class="w-full text-left px-4 py-2 text-white hover:bg-[#F5BE32] hover:text-black transition"
                     >
                       {{ $t("admin.orders.statuses.confirmed") }}
                     </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Today's Orders -->
          <div
            v-if="filteredTodaysOrders.length > 0"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6"
          >
            <div
              v-for="order in filteredTodaysOrders"
              :key="order.id"
              class="bg-[#0F0F0F] p-3 md:p-5 border border-[#2A2A2A] flex flex-col shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] transition-all duration-300"
            >
              <!-- Order Header -->
              <div
                class="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3 md:mb-4"
              >
                <div>
                  <h3 class="text-white font-semibold text-base md:text-lg">
                    {{ $t("chef.order") }} #{{ order.orderNumber }}
                  </h3>
                  <p class="text-gray-400 text-xs md:text-sm">
                    {{
                      order.orderType === "delivery"
                        ? formatDateTime(order.orderCreatedAt)
                        : formatDate(order.date)
                    }}
                  </p>
                  <p class="text-gray-400 text-xs md:text-sm">
                    {{ order.tableName }}
                  </p>
                  <!-- Show delivery address for delivery orders -->
                  <p
                    v-if="order.orderType === 'delivery' && order.deliveryAddress"
                    class="text-[#F5BE32] text-xs md:text-sm"
                  >
                    📍 {{ order.deliveryAddress }}
                  </p>
                </div>
                <div class="flex flex-col items-end gap-1">
                  <span
                    class="text-[#F5BE32] font-bold text-sm md:text-base capitalize"
                  >
                    {{
                      order.orderType === "delivery"
                        ? $t("chef.delivery")
                        : $t(`chef.durations.${order.duration}`)
                    }}
                  </span>
                  <span class="text-xs text-gray-400 px-2 py-1 bg-gray-800 rounded">
                    {{ order.orderType === "delivery" ? "🚚" : "🍽️" }}
                  </span>
                </div>
              </div>

              <!-- Items -->
              <div
                class="flex flex-col gap-2 md:gap-3 flex-1 overflow-y-auto max-h-40 md:max-h-48"
              >
                <div
                  v-for="item in order.orderItems"
                  :key="item.name"
                  class="flex flex-col bg-[#0F0F0F] border border-white px-2 md:px-3 py-2 flex-shrink-0"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2 md:gap-3">
                      <img
                        :src="getItemImage(item.name)"
                        :alt="item.name"
                        class="w-8 h-8 md:w-12 md:h-12 object-cover"
                      />
                      <span class="text-white font-medium text-sm md:text-base">{{
                        getTranslatedDishName(item.name, $t)
                      }}</span>
                    </div>
                    <span class="text-white text-xs md:text-sm font-medium"
                      >{{ $t("chef.quantity") }}: {{ item.quantity }}</span
                    >
                  </div>
                  <!-- Special Request -->
                  <div
                    v-if="item.specialRequest && item.specialRequest !== 'none'"
                    class="mt-2 pt-2 border-t border-gray-600"
                  >
                    <div class="flex items-start gap-2">
                      <span class="text-[#F5BE32] text-xs font-medium"
                        >{{ $t("chef.specialRequest") }}:</span
                      >
                      <span class="text-gray-300 text-xs">{{
                        item.specialRequest
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Status -->
              <div
                class="border-t border-[#F5BE32] pt-2 md:pt-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mt-3 md:mt-4"
              >
                <span class="text-white text-xs md:text-sm">{{
                  $t("chef.status")
                }}</span>
                <div class="flex items-center gap-2">
                  <select
                    :value="order.status"
                    @change="updateOrderStatus(order.id, $event.target.value)"
                    :class="getStatusButtonClass(order.status)"
                    class="text-xs md:text-sm font-medium px-2 md:px-3 py-1 rounded-lg focus:outline-none focus:ring-2 transition-colors w-full sm:w-auto"
                  >
                    <option
                      v-if="order.orderType === 'preorder'"
                      value="Not Started"
                    >
                      {{ $t("chef.notStarted") }}
                    </option>
                    <option
                      v-if="order.orderType === 'preorder'"
                      value="In Progress"
                    >
                      {{ $t("chef.inProgress") }}
                    </option>
                    <option v-if="order.orderType === 'preorder'" value="Served">
                      {{ $t("chef.served") }}
                    </option>
                    <!-- Today's Orders Delivery Status Options -->
                    <template v-if="order.orderType === 'delivery'">
                      <option 
                        v-for="option in getDeliveryStatusOptions(order)" 
                        :key="option.value"
                        :value="option.value"
                        :disabled="option.disabled"
                      >
                        {{ option.text }}
                      </option>
                    </template>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- No Today's Orders -->
          <div v-else class="text-center py-8 md:py-12">
            <div class="text-gray-400 text-lg md:text-xl">
              {{ todaysOrders.length === 0 ? $t("chef.noTodaysOrders") : $t("chef.noOrders") }}
            </div>
            <p class="text-gray-500 mt-2 text-sm md:text-base">
              {{ todaysOrders.length === 0 ? $t("chef.noTodaysOrdersDesc") : $t("chef.noOrdersDesc") }}
            </p>
          </div>
        </div>

        <!-- All Orders Section -->
        <div>
          <div class="flex items-center gap-2 md:gap-3 mb-4">
            <img
              src="@/assets/icons/ListChecks.svg"
              alt="List Checks"
              class="w-8 h-6 md:w-10 md:h-8"
            />
            <h2 class="text-xl md:text-2xl lg:text-3xl font-bold text-[#F5BE32]">
              {{ $t("chef.allOrders") }}
            </h2>
          </div>

          <!-- All Orders -->
          <div
            v-if="filteredAllOrders.length > 0"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6"
          >
            <div
              v-for="order in filteredAllOrders"
              :key="order.id"
              class="bg-[#0F0F0F] p-3 md:p-5 border border-[#2A2A2A] flex flex-col shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] transition-all duration-300"
            >
              <!-- Order Header -->
              <div
                class="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3 md:mb-4"
              >
                <div>
                  <h3 class="text-white font-semibold text-base md:text-lg">
                    {{ $t("chef.order") }} #{{ order.orderNumber }}
                  </h3>
                  <p class="text-gray-400 text-xs md:text-sm">
                    {{
                      order.orderType === "delivery"
                        ? formatDateTime(order.orderCreatedAt)
                        : formatDate(order.date)
                    }}
                  </p>
                  <p class="text-gray-400 text-xs md:text-sm">
                    {{ order.tableName }}
                  </p>
                  <!-- Show delivery address for delivery orders -->
                  <p
                    v-if="order.orderType === 'delivery' && order.deliveryAddress"
                    class="text-[#F5BE32] text-xs md:text-sm"
                  >
                    📍 {{ order.deliveryAddress }}
                  </p>
                </div>
                <div class="flex flex-col items-end gap-1">
                  <span
                    class="text-[#F5BE32] font-bold text-sm md:text-base capitalize"
                  >
                    {{
                      order.orderType === "delivery"
                        ? $t("chef.delivery")
                        : $t(`chef.durations.${order.duration}`)
                    }}
                  </span>
                  <span class="text-xs text-gray-400 px-2 py-1 bg-gray-800 rounded">
                    {{ order.orderType === "delivery" ? "🚚" : "🍽️" }}
                  </span>
                </div>
              </div>

              <!-- Items -->
              <div
                class="flex flex-col gap-2 md:gap-3 flex-1 overflow-y-auto max-h-40 md:max-h-48"
              >
                <div
                  v-for="item in order.orderItems"
                  :key="item.name"
                  class="flex flex-col bg-[#0F0F0F] border border-white px-2 md:px-3 py-2 flex-shrink-0"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2 md:gap-3">
                      <img
                        :src="getItemImage(item.name)"
                        :alt="item.name"
                        class="w-8 h-8 md:w-12 md:h-12 object-cover"
                      />
                      <span class="text-white font-medium text-sm md:text-base">{{
                        getTranslatedDishName(item.name, $t)
                      }}</span>
                    </div>
                    <span class="text-white text-xs md:text-sm font-medium"
                      >{{ $t("chef.quantity") }}: {{ item.quantity }}</span
                    >
                  </div>
                  <!-- Special Request -->
                  <div
                    v-if="item.specialRequest && item.specialRequest !== 'none'"
                    class="mt-2 pt-2 border-t border-gray-600"
                  >
                    <div class="flex items-start gap-2">
                      <span class="text-[#F5BE32] text-xs font-medium"
                        >{{ $t("chef.specialRequest") }}:</span
                      >
                      <span class="text-gray-300 text-xs">{{
                        item.specialRequest
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Status -->
              <div
                class="border-t border-[#F5BE32] pt-2 md:pt-3 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mt-3 md:mt-4"
              >
                <span class="text-white text-xs md:text-sm">{{
                  $t("chef.status")
                }}</span>
                <div class="flex items-center gap-2">
                  <select
                    :value="order.status"
                    @change="updateOrderStatus(order.id, $event.target.value)"
                    :class="getStatusButtonClass(order.status)"
                    class="text-xs md:text-sm font-medium px-2 md:px-3 py-1 rounded-lg focus:outline-none focus:ring-2 transition-colors w-full sm:w-auto"
                  >
                    <option
                      v-if="order.orderType === 'preorder'"
                      value="Not Started"
                    >
                      {{ $t("chef.notStarted") }}
                    </option>
                    <option
                      v-if="order.orderType === 'preorder'"
                      value="In Progress"
                    >
                      {{ $t("chef.inProgress") }}
                    </option>
                    <option v-if="order.orderType === 'preorder'" value="Served">
                      {{ $t("chef.served") }}
                    </option>
                    <!-- All Orders Delivery Status Options -->
                    <template v-if="order.orderType === 'delivery'">
                      <option 
                        v-for="option in getDeliveryStatusOptions(order)" 
                        :key="option.value"
                        :value="option.value"
                        :disabled="option.disabled"
                      >
                        {{ option.text }}
                      </option>
                    </template>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- No All Orders -->
          <div v-else class="text-center py-8 md:py-12">
            <div class="text-gray-400 text-lg md:text-xl">
              {{ $t("chef.noOrders") }}
            </div>
            <p class="text-gray-500 mt-2 text-sm md:text-base">
              {{ $t("chef.noOrdersDesc") }}
            </p>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import chefHeaderBg from "@/assets/chef/chefHeader.png";
import { getTranslatedDishName } from "@/utils/dishTranslation";

export default {
  name: "ChefDashboard",
  data() {
    return {
      todaysOrders: [],
      allOrders: [], // Store all orders for filtering
      loading: true,
      showOrderTypeFilterDropdown: false,
      showDurationFilterDropdown: false,
      showStatusFilterDropdown: false,
      selectedOrderTypeFilter: "",
      selectedDurationFilter: "",
      selectedStatusFilter: "",
    };
  },
  computed: {
    headerStyle() {
      return {
        backgroundImage: `url(${chefHeaderBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
    },
    filteredAllOrders() {
      let filteredOrders = [...this.allOrders];

      // Apply order type filter
      if (this.selectedOrderTypeFilter) {
        if (this.selectedOrderTypeFilter === "preorder") {
          filteredOrders = filteredOrders.filter(
            (order) => order.orderType === "preorder"
          );
        } else if (this.selectedOrderTypeFilter === "delivery") {
          filteredOrders = filteredOrders.filter(
            (order) => order.orderType === "delivery"
          );
        } else if (this.selectedOrderTypeFilter === "dineIn") {
          filteredOrders = filteredOrders.filter(
            (order) =>
              order.orderType === "preorder" && order.duration !== "N/A"
          );
        }
      }

      // Apply duration filter
      if (this.selectedDurationFilter) {
        filteredOrders = filteredOrders.filter(
          (order) =>
            order.duration.toLowerCase() ===
            this.selectedDurationFilter.toLowerCase()
        );
      }

      // Apply status filter
      if (this.selectedStatusFilter) {
        filteredOrders = filteredOrders.filter(
          (order) => order.status === this.selectedStatusFilter
        );
      }

      return filteredOrders;
    },
    filteredTodaysOrders() {
      let filteredOrders = [...this.todaysOrders];

      // Apply order type filter
      if (this.selectedOrderTypeFilter) {
        if (this.selectedOrderTypeFilter === "preorder") {
          filteredOrders = filteredOrders.filter(
            (order) => order.orderType === "preorder"
          );
        } else if (this.selectedOrderTypeFilter === "delivery") {
          filteredOrders = filteredOrders.filter(
            (order) => order.orderType === "delivery"
          );
        } else if (this.selectedOrderTypeFilter === "dineIn") {
          filteredOrders = filteredOrders.filter(
            (order) =>
              order.orderType === "preorder" && order.duration !== "N/A"
          );
        }
      }

      // Apply duration filter
      if (this.selectedDurationFilter) {
        filteredOrders = filteredOrders.filter(
          (order) =>
            order.duration.toLowerCase() ===
            this.selectedDurationFilter.toLowerCase()
        );
      }

      // Apply status filter
      if (this.selectedStatusFilter) {
        filteredOrders = filteredOrders.filter(
          (order) => order.status === this.selectedStatusFilter
        );
      }

      return filteredOrders;
    },
  },
  async mounted() {
    await this.loadAllOrders();
  },
  methods: {
    toggleLang() {
      const currentLang = this.$i18n.locale;
      const newLang = currentLang === "en" ? "ar" : "en";
      this.$i18n.locale = newLang;
      localStorage.setItem("lang", newLang);
      document.documentElement.setAttribute(
        "dir",
        newLang === "ar" ? "rtl" : "ltr"
      );
    },
    async loadAllOrders() {
      try {
        this.loading = true;
        const db = getFirestore();
        const today = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"

        // Load preorders from reservations
        const reservationsRef = collection(db, "reservations");
        const preorderQuery = query(
          reservationsRef,
          where("preorder", "==", true)
        );
        const preorderSnapshot = await getDocs(preorderQuery);

        // Load delivery orders
        const deliveryOrdersRef = collection(db, "deliveryOrders");
        const deliverySnapshot = await getDocs(deliveryOrdersRef);

        const allOrders = [];

        // Process preorders
        for (const doc of preorderSnapshot.docs) {
          const data = doc.data();
          console.log("Preorder data:", data);

          const preorderStatus = await this.getPreorderStatus(doc.id);

          if (preorderStatus != "pending") {
          allOrders.push({
            id: doc.id,
            orderType: "preorder",
            orderCreatedAt: data.orderCreatedAt || data.createdAt || new Date(),
            date: data.date,
            duration: data.duration || "N/A",
            tableName: data.tableName || "N/A",
            guests: data.guests || 0,
            name: data.name || "Unknown",
            phone: data.phone || "",
            status: preorderStatus || "Not Started",
            orderGrandTotal: data.orderGrandTotal || 0,
            preorder: true,
            orderItems: this.parseOrderItems(
              data.order || data.orderItems || data.dishes || []
            ),
          });
        }

          if (!preorderStatus) {
            await this.addToPreorderCollection(doc.id, data);
          }
        }

        // Process delivery orders
        for (const doc of deliverySnapshot.docs) {
          const data = doc.data();
          console.log("Delivery order data:", data);

          allOrders.push({
            id: doc.id,
            orderType: "delivery",
            orderCreatedAt: data.createdAt || new Date(),
            date: data.createdAt || new Date(),
            duration: "N/A",
            tableName: "Delivery",
            guests: 0,
            name: data.userName || "Unknown",
            phone: data.userPhone || "",
            status: data.status || "Not Started",
            orderGrandTotal: data.totalAmount || 0,
            preorder: false,
            deliveryAddress: data.deliveryAddress || "",
            orderItems: this.parseOrderItems(data.dishes || []),
          });
        }

        // Sort by order creation time (oldest first)
        allOrders.sort((a, b) => {
          const dateA =
            a.orderCreatedAt instanceof Date
              ? a.orderCreatedAt
              : new Date(a.orderCreatedAt.seconds * 1000);
          const dateB =
            b.orderCreatedAt instanceof Date
              ? b.orderCreatedAt
              : new Date(b.orderCreatedAt.seconds * 1000);
          return dateA - dateB;
        });

        // Assign order numbers based on sorted order
        allOrders.forEach((order, index) => {
          order.orderNumber = index + 1;
        });

        // Store all orders for filtering
        this.allOrders = allOrders;

        // Separate today's orders from all orders
        this.todaysOrders = allOrders.filter((order) => {
          const orderDate = order.date?.seconds
            ? new Date(order.date.seconds * 1000)
            : new Date(order.date);
          const orderDateStr = orderDate.toISOString().split("T")[0];
          return orderDateStr === today;
        });

        console.log("Processed all orders:", this.allOrders);
        console.log("Today's orders:", this.todaysOrders);
      } catch (error) {
        console.error("Error loading all orders:", error);
      } finally {
        this.loading = false;
      }
    },

    async getPreorderStatus(reservationId) {
      try {
        const db = getFirestore();
        const preorderRef = collection(db, "preorder");
        const q = query(
          preorderRef,
          where("reservationId", "==", reservationId)
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          return snapshot.docs[0].data().status;
        }
        return null;
      } catch (error) {
        console.error("Error getting preorder status:", error);
        return null;
      }
    },

    async addToPreorderCollection(reservationId, reservationData) {
      try {
        // Only add to preorder collection if preorder is true
        if (reservationData.preorder === true) {
          const db = getFirestore();
          const preorderRef = collection(db, "preorder");

          await addDoc(preorderRef, {
            reservationId: reservationId,
            status: "Not Started",
            tableNumber: reservationData.tableName,
            duration: reservationData.duration,
            createdAt: new Date(),
          });

          console.log("Added new order to preorder collection:", reservationId);
        } else {
          console.log("Skipping order - preorder is not true:", reservationId);
        }
      } catch (error) {
        console.error("Error adding to preorder collection:", error);
      }
    },

    async updateOrderStatus(reservationId, newStatus) {
      try {
        const order = this.allOrders.find((o) => o.id === reservationId);
        if (!order) return;

        const db = getFirestore();

        if (order.orderType === "preorder") {
          // Update preorder status
          const preorderRef = collection(db, "preorder");
          const q = query(
            preorderRef,
            where("reservationId", "==", reservationId)
          );
          const snapshot = await getDocs(q);

          if (!snapshot.empty) {
            const docRef = doc(db, "preorder", snapshot.docs[0].id);
            await updateDoc(docRef, {
              status: newStatus,
              updatedAt: new Date(),
            });
          }
        } else if (order.orderType === "delivery") {
          // Update delivery order status
          const deliveryRef = doc(db, "deliveryOrders", reservationId);
          await updateDoc(deliveryRef, {
            status: newStatus,
            updatedAt: new Date(),
          });
        }

        // Update local state in allOrders
        const allOrderToUpdate = this.allOrders.find(
          (o) => o.id === reservationId
        );
        if (allOrderToUpdate) {
          allOrderToUpdate.status = newStatus;
        }

        // Also update in todaysOrders if it exists there
        const todayOrderToUpdate = this.todaysOrders.find(
          (o) => o.id === reservationId
        );
        if (todayOrderToUpdate) {
          todayOrderToUpdate.status = newStatus;
        }

        console.log("Updated order status:", reservationId, "to", newStatus);
      } catch (error) {
        console.error("Error updating order status:", error);
      }
    },
    parseOrderItems(order) {
      console.log("Parsing order items:", order); // Debug log

      if (Array.isArray(order)) {
        const items = order
          .map((item) => {
            console.log("Processing array item:", item); // Debug log
            return {
              name:
                item.dishName ||
                item.name ||
                item.dish ||
                item.title ||
                item.item ||
                "",
              quantity:
                item.count || item.quantity || item.qty || item.amount || 1,
              specialRequest: item.specialRequest || "",
            };
          })
          .filter((item) => item.name && item.name.trim() !== "");
        console.log("Parsed array items:", items); // Debug log
        return items;
      } else if (typeof order === "object" && order !== null) {
        const items = Object.keys(order).map((key) => {
          console.log("Processing object key:", key, "value:", order[key]); // Debug log
          return {
            name: key,
            quantity: order[key],
          };
        });
        console.log("Parsed object items:", items); // Debug log
        return items;
      } else if (typeof order === "string") {
        // Handle string format like "Dragon Sushi 2, Steak Tartare 1"
        console.log("Processing string order:", order); // Debug log
        const items = order
          .split(",")
          .map((item) => {
            const parts = item.trim().split(" ");
            const quantity = parts[parts.length - 1];
            const name = parts.slice(0, -1).join(" ");
            return { name, quantity: parseInt(quantity) || 1 };
          })
          .filter((item) => item.name && item.name.trim() !== "");
        console.log("Parsed string items:", items); // Debug log
        return items;
      }
      return [];
    },
    formatDate(dateStr) {
      // Handle Firestore timestamp objects
      if (dateStr && typeof dateStr === "object" && dateStr.seconds) {
        const d = new Date(dateStr.seconds * 1000);
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        return `${days[d.getDay()]} ${d.getDate()}/${
          d.getMonth() + 1
        }/${d.getFullYear()}`;
      }

      // Handle regular date strings
      const d = new Date(dateStr);
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return `${days[d.getDay()]} ${d.getDate()}/${
        d.getMonth() + 1
      }/${d.getFullYear()}`;
    },

    formatDateTime(dateStr) {
      // Handle Firestore timestamp objects
      if (dateStr && typeof dateStr === "object" && dateStr.seconds) {
        const d = new Date(dateStr.seconds * 1000);
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const hours = d.getHours().toString().padStart(2, "0");
        const minutes = d.getMinutes().toString().padStart(2, "0");
        return `${days[d.getDay()]} ${d.getDate()}/${
          d.getMonth() + 1
        }/${d.getFullYear()} ${hours}:${minutes}`;
      }

      // Handle regular date strings
      const d = new Date(dateStr);
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const hours = d.getHours().toString().padStart(2, "0");
      const minutes = d.getMinutes().toString().padStart(2, "0");
      return `${days[d.getDay()]} ${d.getDate()}/${
        d.getMonth() + 1
      }/${d.getFullYear()} ${hours}:${minutes}`;
    },
    getStatusColor(status) {
      return status?.toLowerCase() === "confirmed" ||
        status?.toLowerCase() === "in-progress"
        ? "bg-[#F5BE32]"
        : "bg-gray-500";
    },
         getStatusText(status) {
       switch (status) {
         case "Not Started":
           return this.$t("chef.notStarted");
         case "In Progress":
           return this.$t("chef.inProgress");
         case "Served":
           return this.$t("chef.served");
         case "pending":
           return this.$t("admin.orders.statuses.pending");
         case "preparing":
           return this.$t("admin.orders.statuses.preparing");
         case "confirmed":
           return this.$t("admin.orders.statuses.confirmed");
         case "cancelled":
           return this.$t("admin.orders.statuses.cancelled");
         case "delivered":
           return this.$t("admin.orders.statuses.delivered");
         default:
           return this.$t("chef.notStarted");
       }
     },
         getStatusButtonClass(status) {
       switch (status) {
         case "Not Started":
           return "bg-gray-500/20 border border-gray-500 text-gray-300 focus:ring-gray-500";
         case "In Progress":
           return "bg-[#F5BE32]/20 border border-[#F5BE32] text-[#F5BE32] focus:ring-[#F5BE32]";
         case "Served":
           return "bg-green-500/20 border border-green-500 text-green-400 focus:ring-green-500";
         case "pending":
           return "bg-gray-500/20 border border-gray-500 text-gray-300 focus:ring-gray-500";
         case "preparing":
           return "bg-blue-500/20 border border-blue-500 text-blue-400 focus:ring-blue-500";
         case "confirmed":
           return "bg-[#F5BE32]/20 border border-[#F5BE32] text-[#F5BE32] focus:ring-[#F5BE32]";
         case "cancelled":
           return "bg-red-500/20 border border-red-500 text-red-400 focus:ring-red-500";
         case "delivered":
           return "bg-green-500/20 border border-green-500 text-green-400 focus:ring-green-500";
         default:
           return "bg-gray-500/20 border border-gray-500 text-gray-300 focus:ring-gray-500";
       }
     },
    getStatusTextClass(status) {
      switch (status) {
        case "Not Started":
          return "bg-gray-500 text-gray-300";
        case "In Progress":
          return "bg-[#F5BE32] text-black";
        case "Ready for Delivery":
          return "bg-blue-500 text-white";
        case "Served":
          return "bg-green-500 text-white";
        case "Delivered":
          return "bg-green-500 text-white";
        default:
          return "bg-gray-500 text-gray-300";
      }
    },

    getItemImage(name) {
      console.log("Getting image for item:", name); // Debug log

      const map = {
        // Appetizers
        "Tomato Toast": require("@/assets/Menu/appetizer/TomatoToast.png"),
        "Noodle Soup": require("@/assets/Menu/appetizer/NoodleSoup.png"),
        "Pumpkin Soup": require("@/assets/Menu/appetizer/PumpkinSoup.png"),

        // Sushi
        "Dragon Sushi": require("@/assets/Menu/sushi/Dragon-Sushi.png"),
        "Creamy Sushi": require("@/assets/Menu/sushi/Creamy-Sushi.png"),
        "Roll Salmon Sushi": require("@/assets/Menu/sushi/Roll-Salmon-Sushi.png"),

        // Steak
        "Steak Tartare": require("@/assets/Menu/steak/Steak-tartare.png"),
        "Surf and Turf": require("@/assets/Menu/steak/SurfTurf.png"),
        SurfTurf: require("@/assets/Menu/steak/SurfTurf.png"),
        "Wagyu Beef": require("@/assets/Menu/steak/WagyuBeef.png"),

        // Chicken
        "Creamy Parmesan Chicken": require("@/assets/Menu/chicken/Creamy-Parmesan.png"),
        "Creamy Parmesan": require("@/assets/Menu/chicken/Creamy-Parmesan.png"),
        "Spring Chicken": require("@/assets/Menu/chicken/Spring-Chicken.png"),
        "Chicken Francese": require("@/assets/Menu/chicken/Chicken-Francese.png"),

        // Beverages
        "Panthouse Tonic": require("@/assets/Menu/beverages/Panthouse-Tonic.png"),
        "Apple Breeze": require("@/assets/Menu/beverages/Apple-Breeze.png"),
        "Frenchman Blitz": require("@/assets/Menu/beverages/Frenchman-Blitz.png"),
      };

      if (map[name]) {
        console.log("Found image for:", name);
        return map[name];
      } else {
        console.log("No image found for:", name, "- using default");
        return require("@/assets/Menu/appetizer/TomatoToast.png");
      }
    },

    getTranslatedDishName(dishName, t) {
      return getTranslatedDishName(dishName, t);
    },
    toggleOrderTypeFilter() {
      this.showOrderTypeFilterDropdown = !this.showOrderTypeFilterDropdown;
      this.showDurationFilterDropdown = false; // Close other dropdown
      this.showStatusFilterDropdown = false; // Close other dropdown
    },

    toggleDurationFilter() {
      this.showDurationFilterDropdown = !this.showDurationFilterDropdown;
      this.showOrderTypeFilterDropdown = false; // Close other dropdown
      this.showStatusFilterDropdown = false; // Close other dropdown
    },

    toggleStatusFilter() {
      this.showStatusFilterDropdown = !this.showStatusFilterDropdown;
      this.showOrderTypeFilterDropdown = false; // Close other dropdown
      this.showDurationFilterDropdown = false; // Close other dropdown
    },

    setOrderTypeFilter(filter) {
      this.selectedOrderTypeFilter = filter;
      this.showOrderTypeFilterDropdown = false;
      this.applyFilters();
    },

    setDurationFilter(filter) {
      this.selectedDurationFilter = filter;
      this.showDurationFilterDropdown = false;
      this.applyFilters();
    },

    setStatusFilter(filter) {
      this.selectedStatusFilter = filter;
      this.showStatusFilterDropdown = false;
      this.applyFilters();
    },

         applyFilters() {
       // The filtering is now handled by the computed property filteredAllOrders
       // This method is kept for compatibility but no longer needed
       console.log("Filters applied - using computed property");
     },

     getOrderTypeFilterText(filter) {
       if (!filter) return "";
       switch (filter) {
         case "preorder":
           return this.$t("chef.preorder");
         case "delivery":
           return this.$t("chef.delivery");
         case "dineIn":
           return this.$t("chef.dineIn");
         default:
           return filter;
       }
     },

     getDurationFilterText(filter) {
       if (!filter) return "";
       switch (filter) {
         case "breakfast":
           return this.$t("chef.breakfast");
         case "lunch":
           return this.$t("chef.lunch");
         case "dinner":
           return this.$t("chef.dinner");
         default:
           return filter;
       }
     },

     getStatusFilterText(filter) {
       if (!filter) return "";
       switch (filter) {
         case "Not Started":
           return this.$t("chef.notStarted");
         case "In Progress":
           return this.$t("chef.inProgress");
         case "Served":
           return this.$t("chef.served");
         case "pending":
           return this.$t("admin.orders.statuses.pending");
         case "preparing":
           return this.$t("admin.orders.statuses.preparing");
         case "confirmed":
           return this.$t("admin.orders.statuses.confirmed");
         case "cancelled":
           return this.$t("admin.orders.statuses.cancelled");
         case "delivered":
           return this.$t("admin.orders.statuses.delivered");
         default:
           return filter;
       }
     },

     getDeliveryStatusOptions(order) {
       const options = [];
       
       // Current status option (always shown)
       if (order.status === 'pending') {
         options.push({ value: 'pending', text: this.$t("admin.orders.statuses.pending") });
       } else if (order.status === 'preparing') {
         options.push({ value: 'preparing', text: this.$t("admin.orders.statuses.preparing") });
       } else if (order.status === 'confirmed') {
         options.push({ value: 'confirmed', text: this.$t("admin.orders.statuses.confirmed") });
       } else if (order.status === 'cancelled') {
         options.push({ value: 'cancelled', text: this.$t("admin.orders.statuses.cancelled"), disabled: true });
       } else if (order.status === 'delivered') {
         options.push({ value: 'delivered', text: this.$t("admin.orders.statuses.delivered"), disabled: true });
       }
       
       // Available next status options
       if (order.status === 'pending') {
         options.push({ value: 'preparing', text: this.$t("admin.orders.statuses.preparing") });
         options.push({ value: 'confirmed', text: this.$t("admin.orders.statuses.confirmed") });
       } else if (order.status === 'preparing') {
        options.push({ value: 'pending', text: this.$t("admin.orders.statuses.pending") });
         options.push({ value: 'confirmed', text: this.$t("admin.orders.statuses.confirmed") });
       }else if (order.status === 'confirmed') {
        options.push({ value: 'pending', text: this.$t("admin.orders.statuses.pending") });
         options.push({ value: 'preparing', text: this.$t("admin.orders.statuses.preparing") });
       }
       
       return options;
     },

    handleLogout() {
      // Clear admin session
      localStorage.removeItem("adminLoggedIn");
      localStorage.removeItem("userType");

      // Redirect to admin login
      this.$router.push("/admin/login");
    },
  },
};
</script>

<style>
body {
  margin: 0;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

/* Style for select dropdown */
select {
  color: white !important;
  border: 1px solid #f5be32 !important;
  border-radius: 0.5rem !important;
  padding: 0.5rem 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  outline: none !important;
  transition: all 0.2s ease !important;
}

/* Style for select dropdown options - keep default browser styling */
select option {
  background-color: white !important;
  color: black !important;
  padding: 0.5rem 0.75rem !important;
  font-size: 0.875rem !important;
}

/* Hover state for dropdown options */
select option:hover {
  background-color: #f3f4f6 !important;
}

/* Selected option styling */
select option:checked {
  background-color: #e5e7eb !important;
  color: black !important;
}

/* Focus state for select */
select:focus {
  border-color: #f5be32 !important;
  box-shadow: 0 0 0 2px rgba(245, 190, 50, 0.2) !important;
}

/* Remove the specific status colors for dropdown options */
select option[value="Not Started"],
select option[value="In Progress"],
select option[value="Ready for Delivery"],
select option[value="Served"],
select option[value="Delivered"] {
  color: black !important;
}

/* Ensure dropdown maintains light theme for options */
select:focus option {
  background-color: white !important;
}

select:focus option:hover {
  background-color: #f3f4f6 !important;
}

select:focus option:checked {
  background-color: #e5e7eb !important;
  color: black !important;
}
</style>
